/**
 * Zeit- und Datumsfunktionen. Alle fachlichen Angaben (Datum, Beginn, Ende)
 * sind lokale Zeiten in Europe/Berlin. Für die Dauerberechnung werden sie in
 * echte Zeitpunkte (UTC-Millisekunden) umgerechnet – dadurch stimmen Schichten
 * über Mitternacht und über die Sommer-/Winterzeit-Umstellung.
 *
 * Bewusst ohne externe Bibliothek: nur Intl.DateTimeFormat.
 */

export const TZ = 'Europe/Berlin';

const MINUTE = 60_000;
const DAY = 86_400_000;

const partsFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

export interface LocalParts {
  y: number;
  m: number;
  d: number;
  h: number;
  mi: number;
  s: number;
}

/** Zerlegt einen Zeitpunkt in lokale Berliner Datums-/Uhrzeitbestandteile. */
export function berlinParts(ms: number): LocalParts {
  const out: Record<string, number> = {};
  for (const p of partsFormatter.formatToParts(new Date(ms))) {
    if (p.type !== 'literal') out[p.type] = Number(p.value);
  }
  return { y: out.year, m: out.month, d: out.day, h: out.hour % 24, mi: out.minute, s: out.second };
}

const pad = (n: number, w = 2) => String(n).padStart(w, '0');

/** Lokales Berliner Datum als 'YYYY-MM-DD'. */
export function berlinDate(ms: number): string {
  const p = berlinParts(ms);
  return `${p.y}-${pad(p.m)}-${pad(p.d)}`;
}

/** Lokale Berliner Uhrzeit als 'HH:MM'. */
export function berlinTime(ms: number): string {
  const p = berlinParts(ms);
  return `${pad(p.h)}:${pad(p.mi)}`;
}

/** Offset (Minuten) von Europe/Berlin gegenüber UTC zum Zeitpunkt ms. */
export function berlinOffsetMinutes(ms: number): number {
  const p = berlinParts(ms);
  const asUtc = Date.UTC(p.y, p.m - 1, p.d, p.h, p.mi, p.s);
  return Math.round((asUtc - Math.floor(ms / 1000) * 1000) / MINUTE);
}

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

export function isValidDate(date: string): boolean {
  const m = DATE_RE.exec(date);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const dt = new Date(Date.UTC(y, mo - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d;
}

export function isValidTime(time: string): boolean {
  return TIME_RE.test(time);
}

/** 'HH:MM' → Minuten seit Mitternacht. */
export function timeToMinutes(time: string): number {
  const m = TIME_RE.exec(time);
  if (!m) throw new Error(`Ungültige Uhrzeit: ${time}`);
  return Number(m[1]) * 60 + Number(m[2]);
}

function dateParts(date: string): [number, number, number] {
  const m = DATE_RE.exec(date);
  if (!m) throw new Error(`Ungültiges Datum: ${date}`);
  return [Number(m[1]), Number(m[2]), Number(m[3])];
}

/**
 * Lokale Berliner Zeit → UTC-Millisekunden.
 * - Mehrdeutige Zeit (Rückstellung, z. B. 25.10.2026 02:30): die frühere
 *   (Sommerzeit-)Interpretation wird gewählt.
 * - Nicht existierende Zeit (Vorstellung, z. B. 29.03.2026 02:30): wird wie
 *   bei Uhren üblich nach vorne verschoben (→ 03:30 Sommerzeit).
 */
export function berlinToUtc(date: string, time: string): number {
  const [y, mo, d] = dateParts(date);
  const minutes = timeToMinutes(time);
  const localAsUtc = Date.UTC(y, mo - 1, d, Math.floor(minutes / 60), minutes % 60);
  const offsets = new Set([
    berlinOffsetMinutes(localAsUtc - DAY),
    berlinOffsetMinutes(localAsUtc),
    berlinOffsetMinutes(localAsUtc + DAY),
  ]);
  const candidates: number[] = [];
  for (const off of offsets) {
    const utc = localAsUtc - off * MINUTE;
    const p = berlinParts(utc);
    if (p.y === y && p.m === mo && p.d === d && p.h * 60 + p.mi === minutes) candidates.push(utc);
  }
  if (candidates.length) return Math.min(...candidates);
  // Lücke bei Zeitumstellung: mit dem Offset *vor* der Umstellung rechnen.
  return localAsUtc - berlinOffsetMinutes(localAsUtc - DAY) * MINUTE;
}

/** Datum um n Tage verschieben (reine Kalenderarithmetik, zeitzonenunabhängig). */
export function addDays(date: string, n: number): string {
  const [y, m, d] = dateParts(date);
  const dt = new Date(Date.UTC(y, m - 1, d + n));
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
}

/** Kalendertage von a nach b (b - a). */
export function daysBetween(a: string, b: string): number {
  const [y1, m1, d1] = dateParts(a);
  const [y2, m2, d2] = dateParts(b);
  return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / DAY);
}

/** Wochentag: 0 = Sonntag … 6 = Samstag. */
export function weekday(date: string): number {
  const [y, m, d] = dateParts(date);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

export const WEEKDAYS_SHORT = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
export const WEEKDAYS_LONG = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
export const MONTHS = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

/** 'YYYY-MM' eines Datums. */
export function monthOf(date: string): string {
  return date.slice(0, 7);
}

export function isValidMonth(month: string): boolean {
  return /^\d{4}-(0[1-9]|1[0-2])$/.test(month);
}

/** Alle Tage eines Monats 'YYYY-MM'. */
export function daysOfMonth(month: string): string[] {
  const [y, m] = month.split('-').map(Number);
  const n = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return Array.from({ length: n }, (_, i) => `${month}-${pad(i + 1)}`);
}

export function shiftMonth(month: string, n: number): string {
  const [y, m] = month.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}`;
}

export function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return `${MONTHS[m - 1]} ${y}`;
}

/** '2026-10-25' → '25.10.2026' */
export function formatDateDe(date: string): string {
  const [y, m, d] = dateParts(date);
  return `${pad(d)}.${pad(m)}.${y}`;
}

export interface Interval {
  startMs: number;
  endMs: number;
  overnight: boolean;
}

/**
 * Zeitintervall eines Eintrags. Ist Ende ≤ Beginn, endet die Schicht am
 * Folgetag (über Mitternacht). Beginn = Ende ist ungültig.
 */
export function entryInterval(date: string, start: string, end: string): Interval {
  if (start === end) throw new Error('Beginn und Ende dürfen nicht gleich sein.');
  const overnight = timeToMinutes(end) < timeToMinutes(start);
  const startMs = berlinToUtc(date, start);
  const endMs = berlinToUtc(overnight ? addDays(date, 1) : date, end);
  return { startMs, endMs, overnight };
}

/** Anwesenheit brutto (Minuten, ohne Pausenabzug) – DST-korrekt. */
export function grossMinutes(date: string, start: string, end: string): number {
  const iv = entryInterval(date, start, end);
  return Math.round((iv.endMs - iv.startMs) / MINUTE);
}

/** Arbeitszeit netto (Minuten) = brutto − Pause, nie negativ. */
export function workMinutes(date: string, start: string, end: string, pauseMin: number): number {
  return Math.max(0, grossMinutes(date, start, end) - (pauseMin || 0));
}

/** Minuten → '4:30' */
export function formatHM(min: number): string {
  const sign = min < 0 ? '-' : '';
  const a = Math.abs(Math.round(min));
  return `${sign}${Math.floor(a / 60)}:${pad(a % 60)}`;
}

/** Minuten → Dezimalstunden mit deutschem Komma, z. B. 270 → '4,50'. */
export function formatHoursDe(min: number, digits = 2): string {
  return (min / 60).toFixed(digits).replace('.', ',');
}

/** Zahl mit deutschem Dezimalkomma. */
export function formatNumberDe(n: number, digits = 2): string {
  return n.toFixed(digits).replace('.', ',');
}
